/**
 * Game Engine - Core game loop running at 60 FPS
 * Handles frame timing, delta time calculation, and performance monitoring
 */

type UpdateCallback = (deltaTime: number) => void;

export class GameEngine {
  private rafId: number | null = null;
  private lastFrameTime: number = 0;
  private deltaTime: number = 0;
  private isRunning: boolean = false;
  private isPaused: boolean = false;

  // FPS monitoring
  private frameCount: number = 0;
  private fpsLastTime: number = 0;
  private currentFPS: number = 60;

  // Update callback
  private updateCallback: UpdateCallback | null = null;

  constructor() {
    this.lastFrameTime = performance.now();
    this.fpsLastTime = performance.now();
  }

  /**
   * Start the game loop
   */
  public startGameLoop(updateCallback: UpdateCallback): void {
    if (this.isRunning) {
      console.warn('Game loop already running');
      return;
    }

    this.updateCallback = updateCallback;
    this.isRunning = true;
    this.isPaused = false;
    this.lastFrameTime = performance.now();
    this.fpsLastTime = performance.now();
    this.frameCount = 0;

    this.gameLoop();
  }

  /**
   * Stop the game loop
   */
  public stopGameLoop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.isRunning = false;
    this.isPaused = false;
    this.updateCallback = null;
  }

  /**
   * Pause the game loop
   */
  public pause(): void {
    if (!this.isRunning) return;
    this.isPaused = true;
  }

  /**
   * Resume the game loop
   */
  public resume(): void {
    if (!this.isRunning) return;
    if (!this.isPaused) return;

    this.isPaused = false;
    this.lastFrameTime = performance.now(); // Reset to prevent large delta
  }

  /**
   * Get current FPS
   */
  public getFPS(): number {
    return this.currentFPS;
  }

  /**
   * Get delta time in seconds
   */
  public getDeltaTime(): number {
    return this.deltaTime;
  }

  /**
   * Check if game loop is running
   */
  public getIsRunning(): boolean {
    return this.isRunning;
  }

  /**
   * Check if game loop is paused
   */
  public getIsPaused(): boolean {
    return this.isPaused;
  }

  /**
   * Main game loop using RequestAnimationFrame
   */
  private gameLoop = (): void => {
    if (!this.isRunning) return;

    const currentTime = performance.now();

    // Calculate delta time in seconds (capped at 100ms to prevent spiral of death)
    const rawDelta = currentTime - this.lastFrameTime;
    this.deltaTime = Math.min(rawDelta, 100) / 1000;
    this.lastFrameTime = currentTime;

    // Update FPS counter
    this.updateFPS(currentTime);

    // Execute update callback if not paused
    if (!this.isPaused && this.updateCallback) {
      try {
        this.updateCallback(this.deltaTime);
      } catch (error) {
        console.error('Error in game update callback:', error);
      }
    }

    // Schedule next frame
    this.rafId = requestAnimationFrame(this.gameLoop);
  };

  /**
   * Update FPS calculation
   */
  private updateFPS(currentTime: number): void {
    this.frameCount++;

    // Update FPS every second
    const fpsElapsed = currentTime - this.fpsLastTime;
    if (fpsElapsed >= 1000) {
      this.currentFPS = Math.round((this.frameCount * 1000) / fpsElapsed);
      this.frameCount = 0;
      this.fpsLastTime = currentTime;

      // Log warning if FPS drops below 50
      if (this.currentFPS < 50) {
        console.warn(`Low FPS detected: ${this.currentFPS} FPS`);
      }
    }
  }
}

// Singleton instance
export const gameEngine = new GameEngine();
